import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2 } from "lucide-react";
import ScorecardAdmin from "@/components/ScorecardAdmin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ baseURL: API, withCredentials: true });

const AUDIT_STATUSES = ["Submitted", "Automated Review Pending", "Automated Review Complete", "Human Review Required", "Approved", "Sent"];
const DIMENSION_STATUSES = ["Needs Verification", "Strong", "Needs Attention", "High Risk"];
const SOURCE_TYPES = ["Needs Verification", "Observed", "Self-Reported", "Calculated"];
const CONFIDENCE_LEVELS = ["Low", "Medium", "High"];
const STATUS_COLORS = {
  Submitted: "bg-sky-500/15 text-sky-300",
  "Automated Review Pending": "bg-amber-500/15 text-amber-300",
  "Automated Review Complete": "bg-amber-500/15 text-amber-300",
  "Human Review Required": "bg-orange-500/15 text-orange-300",
  Approved: "bg-emerald-500/15 text-emerald-300",
  Sent: "bg-emerald-500/15 text-emerald-300",
};

const inputCls =
  "w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent";
const labelCls = "block text-[11px] font-semibold text-slate-400 mb-1 uppercase tracking-wider";

function Badge({ status }) {
  return (
    <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${STATUS_COLORS[status] || "bg-white/10 text-slate-300"}`}>
      {status}
    </span>
  );
}

function DimensionEditor({ dimKey, dim, onChange }) {
  const set = (k) => (e) => onChange(dimKey, { ...dim, [k]: e.target.value });
  return (
    <div data-testid={`dimension-${dimKey}`} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
      <h3 className="font-heading font-bold text-white">{dim.label}</h3>
      <p className="text-xs text-slate-400 mt-0.5 mb-4">{dim.question}</p>
      {dim.evidence_items && dim.evidence_items.length > 0 && (
        <div className="mb-4 space-y-1.5">
          {dim.evidence_items.map((ev, i) => (
            <p key={i} className="text-xs text-slate-400 leading-relaxed">
              <span className="inline-block rounded-full bg-white/10 text-slate-300 px-2 py-0.5 mr-1.5 font-semibold">
                {ev.source_type}
              </span>
              {ev.text}
              {ev.url && (
                <>
                  {" — "}
                  <a href={ev.url} target="_blank" rel="noopener noreferrer" className="text-cyan-300 underline underline-offset-2 break-all">
                    {ev.url}
                  </a>
                </>
              )}
            </p>
          ))}
        </div>
      )}
      <div className="grid sm:grid-cols-4 gap-3">
        <div>
          <label className={labelCls}>Score (0–100)</label>
          <input type="number" min="0" max="100" value={dim.score ?? ""} onChange={set("score")} placeholder="—" className={inputCls} data-testid={`dim-${dimKey}-score`} />
        </div>
        <div>
          <label className={labelCls}>Status</label>
          <select value={dim.status} onChange={set("status")} className={inputCls} data-testid={`dim-${dimKey}-status`}>
            {DIMENSION_STATUSES.map((s) => <option key={s} value={s} className="bg-slate-900">{s}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Confidence</label>
          <select value={dim.confidence} onChange={set("confidence")} className={inputCls} data-testid={`dim-${dimKey}-confidence`}>
            {CONFIDENCE_LEVELS.map((s) => <option key={s} value={s} className="bg-slate-900">{s}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Source Type</label>
          <select value={dim.source_type} onChange={set("source_type")} className={inputCls} data-testid={`dim-${dimKey}-source`}>
            {SOURCE_TYPES.map((s) => <option key={s} value={s} className="bg-slate-900">{s}</option>)}
          </select>
        </div>
      </div>
      <div className="mt-3 space-y-3">
        <div>
          <label className={labelCls}>Findings</label>
          <textarea rows={2} value={dim.findings} onChange={set("findings")} className={inputCls} data-testid={`dim-${dimKey}-findings`} />
        </div>
        <div>
          <label className={labelCls}>Evidence</label>
          <textarea rows={2} value={dim.evidence} onChange={set("evidence")} className={inputCls} data-testid={`dim-${dimKey}-evidence`} />
        </div>
        <div>
          <label className={labelCls}>Recommendation</label>
          <textarea rows={2} value={dim.recommendation} onChange={set("recommendation")} className={inputCls} data-testid={`dim-${dimKey}-recommendation`} />
        </div>
      </div>
    </div>
  );
}

function ObsGroup({ title, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-4">
      <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">{title}</p>
      <ul className="space-y-1 text-sm text-slate-300 leading-relaxed">
        {items.map((it, i) => (
          <li key={i}>{typeof it === "string" ? it : `${it.method}: ${it.detail}`}</li>
        ))}
      </ul>
    </div>
  );
}

function PublicAnalysisPanel({ pa, summary }) {
  return (
    <div data-testid="public-analysis-panel" className="rounded-2xl border border-amber-400/25 bg-amber-500/[0.04] p-6">
      <h3 className="font-heading font-bold text-lg text-white">Automated Public Review</h3>
      <p className="mt-1.5 text-xs font-semibold text-amber-300">
        Automated findings are preliminary and must be reviewed before client delivery.
      </p>
      <div className="mt-4 grid sm:grid-cols-3 gap-x-6 gap-y-2 text-sm text-slate-200">
        <p><span className="text-slate-400">Status:</span> {pa.status}</p>
        <p><span className="text-slate-400">Confidence:</span> {pa.analysis_confidence}</p>
        <p><span className="text-slate-400">Site accessible:</span> {pa.site_accessible ? "Yes" : "No"}</p>
        <p><span className="text-slate-400">HTTPS:</span> {pa.https_status}</p>
        <p><span className="text-slate-400">Pages reviewed:</span> {pa.pages_reviewed}</p>
        <p><span className="text-slate-400">Completed:</span> {pa.analysis_completed_at ? new Date(pa.analysis_completed_at).toLocaleString() : "—"}</p>
      </div>
      {pa.page_urls && pa.page_urls.length > 0 && (
        <div className="mt-4">
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Pages Reviewed</p>
          <ul className="space-y-1 text-sm">
            {pa.page_urls.map((u) => (
              <li key={u}>
                <a href={u} target="_blank" rel="noopener noreferrer" className="text-cyan-300 underline underline-offset-2 break-all">{u}</a>
              </li>
            ))}
          </ul>
        </div>
      )}
      <ObsGroup title="Lead Capture Methods Found (Public Observation)" items={pa.lead_capture_methods_found} />
      <ObsGroup title="Call-to-Action Observations" items={pa.cta_observations} />
      <ObsGroup title="Scheduling Observations" items={pa.scheduling_observations} />
      <ObsGroup title="Contact Friction" items={pa.contact_friction_observations} />
      <ObsGroup title="Trust Signals" items={pa.trust_signal_observations} />
      <ObsGroup title="Mobile Signals" items={pa.mobile_observations} />
      <ObsGroup title="Business Clarity" items={pa.business_clarity_observations} />
      {pa.technical_errors && pa.technical_errors.length > 0 && (
        <div className="mt-4">
          <p className="text-[11px] font-semibold text-red-300 uppercase tracking-wider mb-1.5">Technical Errors</p>
          <ul className="space-y-1 text-sm text-red-200/80">
            {pa.technical_errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}
      {pa.crawler_notes && <p className="mt-4 text-xs text-slate-400 leading-relaxed">{pa.crawler_notes}</p>}
      {summary && (
        <div className="mt-5 border-t border-white/10 pt-4 text-sm text-slate-200 space-y-1">
          <p><span className="text-slate-400">Overall Lead Flow Health (preliminary):</span> <span className="font-semibold">{summary.overall_lead_flow_health}</span></p>
          <p><span className="text-slate-400">Primary Constraint:</span> {summary.primary_constraint || "Not enough evidence"}</p>
          <p><span className="text-slate-400">Secondary Constraint:</span> {summary.secondary_constraint || "Not enough evidence"}</p>
        </div>
      )}
    </div>
  );
}

export default function AdminAudits() {
  const [auth, setAuth] = useState("checking"); // checking | out | in
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [audits, setAudits] = useState([]);
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("audits"); // audits | scorecard
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [searchParams] = useSearchParams();

  useEffect(() => {
    http.get("/auth/me")
      .then(() => setAuth("in"))
      .catch(() => setAuth("out"));
  }, []);

  useEffect(() => {
    if (auth === "in") loadAudits();
  }, [auth]);

  useEffect(() => {
    const target = searchParams.get("audit");
    if (auth === "in" && target && !selected) {
      openAudit(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, searchParams]);

  const loadAudits = async () => {
    const { data } = await http.get("/audits");
    setAudits(data);
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      await http.post("/auth/login", { email, password });
      setAuth("in");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setLoginError(typeof detail === "string" ? detail : "Login failed. Check your credentials.");
    }
  };

  const openAudit = async (id) => {
    const { data } = await http.get(`/audits/${id}`);
    setSelected(data);
    setSavedMsg("");
  };

  const updateDim = (key, val) =>
    setSelected((s) => ({ ...s, dimensions: { ...s.dimensions, [key]: val } }));

  const save = async (extra = {}) => {
    setSaving(true);
    setSavedMsg("");
    try {
      const { data } = await http.patch(`/audits/${selected.audit_id}`, {
        dimensions: selected.dimensions,
        reviewer_notes: selected.reviewer_notes,
        assigned_reviewer: selected.assigned_reviewer,
        ...extra,
      });
      setSelected(data);
      setSavedMsg(extra.audit_status ? `Status updated to ${extra.audit_status}.` : "Saved.");
      loadAudits();
    } catch (err) {
      setSavedMsg("Save failed. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const generateReport = async () => {
    setSaving(true);
    setSavedMsg("");
    try {
      const { data } = await http.post(`/audits/${selected.audit_id}/report/generate`);
      setSelected(data);
      setSavedMsg(`Report generated (v${data.report_version}).`);
      loadAudits();
    } catch (err) {
      setSavedMsg(err.response?.data?.detail || "Report generation failed.");
    } finally {
      setSaving(false);
    }
  };

  const previewReport = () => {
    window.open(`${API}/audits/${selected.audit_id}/report/preview`, "_blank");
  };

  const sendReport = async (resend = false) => {
    const msg = resend
      ? "This report was already sent. Are you sure you want to send it again?"
      : "Send the report email to the prospect now? This cannot be undone.";
    if (!window.confirm(msg)) return;
    if (resend && !window.confirm("Please confirm once more: resend the report to the prospect?")) return;
    setSaving(true);
    setSavedMsg("");
    try {
      const { data } = await http.post(`/audits/${selected.audit_id}/report/send`, null, { params: { confirm_resend: resend } });
      setSelected(data);
      setSavedMsg("Report sent.");
      loadAudits();
    } catch (err) {
      setSavedMsg(err.response?.data?.detail || "Send failed.");
    } finally {
      setSaving(false);
    }
  };

  if (auth === "checking") {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-cyan-400" />
      </div>
    );
  }

  if (auth === "out") {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center px-6">
        <form data-testid="admin-login-form" onSubmit={doLogin} className="w-full max-w-sm rounded-2xl border border-white/10 bg-white/[0.03] p-8 space-y-4">
          <div className="flex items-center gap-2.5 mb-2">
            <span className="bg-white/95 rounded-xl px-2 py-1 flex items-center">
              <img src="/ivibes-cloud.png" alt="iVIBES logo" className="h-6 w-auto" />
            </span>
            <span className="font-heading font-extrabold">Audit Review Queue</span>
          </div>
          <div>
            <label htmlFor="admin-email" className={labelCls}>Email</label>
            <input id="admin-email" data-testid="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label htmlFor="admin-password" className={labelCls}>Password</label>
            <input id="admin-password" data-testid="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
          </div>
          {loginError && <p data-testid="admin-login-error" className="text-sm text-red-300">{loginError}</p>}
          <button type="submit" data-testid="admin-login-button" className="w-full rounded-full px-6 py-3 bg-indigo-600 hover:bg-indigo-500 font-semibold text-white transition-colors duration-200">
            Sign In
          </button>
          <Link to="/" className="block text-center text-xs text-slate-500 hover:text-slate-300">Back to site</Link>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-queue" className="min-h-screen bg-[#0B0F19]">
      <header className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-5">
            <h1 className="font-heading font-extrabold text-lg">Admin</h1>
            <nav className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] p-1">
              <button
                onClick={() => setView("audits")}
                data-testid="admin-tab-audits"
                className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors duration-200 ${view === "audits" ? "bg-indigo-600 text-white" : "text-slate-300 hover:text-white"}`}
              >
                Audits
              </button>
              <button
                onClick={() => setView("scorecard")}
                data-testid="admin-tab-scorecard"
                className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors duration-200 ${view === "scorecard" ? "bg-indigo-600 text-white" : "text-slate-300 hover:text-white"}`}
              >
                Scorecard
              </button>
            </nav>
          </div>
          <div className="flex items-center gap-4">
            {selected && (
              <button onClick={() => setSelected(null)} data-testid="admin-back-to-queue" className="text-sm text-slate-300 hover:text-cyan-300 inline-flex items-center gap-1.5">
                <ArrowLeft size={15} /> Queue
              </button>
            )}
            <button
              data-testid="admin-logout-button"
              onClick={async () => { await http.post("/auth/logout"); setAuth("out"); setSelected(null); }}
              className="text-sm text-slate-400 hover:text-slate-200"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {view === "scorecard" ? (
          <ScorecardAdmin />
        ) : !selected ? (
          <div className="space-y-3">
            {audits.length === 0 && <p className="text-slate-400">No audit requests yet.</p>}
            {audits.map((a) => (
              <button
                key={a.audit_id}
                data-testid={`audit-row-${a.audit_id}`}
                onClick={() => openAudit(a.audit_id)}
                className="w-full text-left rounded-2xl border border-white/10 bg-white/[0.03] hover:border-cyan-400/40 p-5 transition-colors duration-200"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-white">{a.business_name}</p>
                    <p className="text-sm text-slate-400 mt-0.5">
                      {a.first_name} · {a.business_email} · {a.website_url || "No website"}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {new Date(a.submission_date).toLocaleString()} · {(a.selected_challenges || []).join(", ") || "No challenges selected"}
                    </p>
                  </div>
                  <Badge status={a.audit_status} />
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div data-testid="admin-audit-detail" className="space-y-5">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 className="font-heading font-bold text-2xl text-white">{selected.business_name}</h2>
                  <p className="text-slate-300 mt-1">{selected.first_name} · {selected.business_email}</p>
                  <p className="text-sm text-slate-400 mt-1">
                    {selected.website_url || "No website"} · Submitted {new Date(selected.submission_date).toLocaleString()}
                  </p>
                  <p className="text-sm text-slate-400 mt-1">
                    Challenges: {(selected.selected_challenges || []).join(", ") || "None"}
                  </p>
                  {(selected.average_customer_value != null || selected.monthly_inquiries != null || selected.estimated_close_rate != null) && (
                    <p className="text-sm text-slate-400 mt-1">
                      Economics (self-reported): commission/closing {selected.average_customer_value ?? "—"} · leads/mo {selected.monthly_inquiries ?? "—"} · close rate {selected.estimated_close_rate ?? "—"}%
                    </p>
                  )}
                  {(() => {
                    const intake = [
                      ["Agent/Team", selected.operation_type],
                      ["Lead focus", selected.lead_type_focus],
                      ["Monthly lead volume", selected.lead_volume],
                      ["Lead sources", (selected.lead_sources || []).join(", ")],
                      ["Response time", selected.response_time],
                      ["Follow-up process", selected.follow_up_process],
                      ["Follow-up attempts", selected.follow_up_attempts],
                      ["CRM", selected.crm_used],
                      ["Booking process", selected.booking_process],
                      ["After-hours", selected.after_hours],
                      ["Dormant leads", selected.dormant_leads],
                    ].filter(([, v]) => v);
                    return intake.length > 0 ? (
                      <div data-testid="admin-intake-answers" className="mt-3 flex flex-wrap gap-2">
                        {intake.map(([k, v]) => (
                          <span key={k} className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-slate-300">
                            <span className="text-slate-500">{k}:</span> {v}
                          </span>
                        ))}
                      </div>
                    ) : null;
                  })()}
                </div>
                <Badge status={selected.audit_status} />
              </div>
              <div className="grid sm:grid-cols-2 gap-3 mt-5">
                <div>
                  <label className={labelCls}>Audit Status</label>
                  <select
                    data-testid="admin-status-select"
                    value={selected.audit_status}
                    onChange={(e) => setSelected((s) => ({ ...s, audit_status: e.target.value }))}
                    className={inputCls}
                  >
                    {AUDIT_STATUSES.map((s) => <option key={s} value={s} className="bg-slate-900">{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Assigned Reviewer</label>
                  <input
                    data-testid="admin-reviewer-input"
                    value={selected.assigned_reviewer}
                    onChange={(e) => setSelected((s) => ({ ...s, assigned_reviewer: e.target.value }))}
                    className={inputCls}
                  />
                </div>
              </div>
              {selected.report_file_reference && (
                <p className="text-xs text-slate-500 mt-4" data-testid="admin-report-meta">
                  Report v{selected.report_version} · generated {new Date(selected.report_generated_at).toLocaleString()}
                  {selected.report_sent_at && ` · sent ${new Date(selected.report_sent_at).toLocaleString()}`}
                  {selected.report_reviewer && ` · by ${selected.report_reviewer}`}
                </p>
              )}
            </div>

            {selected.public_analysis && (
              <PublicAnalysisPanel pa={selected.public_analysis} summary={selected.summary} />
            )}

            {Object.entries(selected.dimensions).map(([key, dim]) => (
              <DimensionEditor key={key} dimKey={key} dim={dim} onChange={updateDim} />
            ))}

            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
              <label className={labelCls}>Reviewer Notes</label>
              <textarea
                data-testid="admin-reviewer-notes"
                rows={4}
                value={selected.reviewer_notes}
                onChange={(e) => setSelected((s) => ({ ...s, reviewer_notes: e.target.value }))}
                className={inputCls}
              />
            </div>

            <div className="flex flex-wrap items-center gap-3 pb-10">
              <button
                data-testid="admin-save-button"
                onClick={() => save({ audit_status: selected.audit_status })}
                disabled={saving}
                className="rounded-full px-7 py-3 bg-indigo-600 hover:bg-indigo-500 font-semibold text-white transition-colors duration-200 disabled:opacity-60"
              >
                {saving ? "Saving…" : "Save Changes"}
              </button>
              <button
                data-testid="admin-approve-button"
                onClick={() => save({ audit_status: "Approved" })}
                disabled={saving}
                className="rounded-full px-7 py-3 bg-emerald-600 hover:bg-emerald-500 font-semibold text-white transition-colors duration-200 disabled:opacity-60"
              >
                Approve Report
              </button>
              {selected.audit_status === "Approved" && (
                <button
                  data-testid="admin-generate-report-button"
                  onClick={generateReport}
                  disabled={saving}
                  className="rounded-full px-7 py-3 bg-cyan-600 hover:bg-cyan-500 font-semibold text-white transition-colors duration-200 disabled:opacity-60"
                >
                  {selected.report_file_reference ? "Regenerate Report" : "Generate Report"}
                </button>
              )}
              {selected.report_file_reference && (
                <button
                  data-testid="admin-preview-report-button"
                  onClick={previewReport}
                  className="rounded-full px-7 py-3 bg-white/10 hover:bg-white/15 font-semibold text-white transition-colors duration-200"
                >
                  Preview Report
                </button>
              )}
              {selected.audit_status === "Approved" && selected.report_file_reference && (
                <button
                  data-testid="admin-send-report-button"
                  onClick={() => sendReport(false)}
                  disabled={saving}
                  className="rounded-full px-7 py-3 bg-emerald-600 hover:bg-emerald-500 font-semibold text-white transition-colors duration-200 disabled:opacity-60"
                >
                  Approve &amp; Send Report
                </button>
              )}
              {selected.audit_status === "Sent" && (
                <>
                  <span data-testid="admin-report-sent-note" className="text-sm text-emerald-300 font-semibold">
                    Report sent{selected.report_sent_at ? ` · ${new Date(selected.report_sent_at).toLocaleString()}` : ""}
                  </span>
                  <button
                    data-testid="admin-resend-report-button"
                    onClick={() => sendReport(true)}
                    disabled={saving}
                    className="rounded-full px-7 py-3 bg-white/10 hover:bg-white/15 font-semibold text-white transition-colors duration-200 disabled:opacity-60"
                  >
                    Resend Report
                  </button>
                </>
              )}
              {savedMsg && <span data-testid="admin-save-msg" className="text-sm text-emerald-300">{savedMsg}</span>}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
